<template>
  <div>
    <ul class="nav nav-tabs mb-2" role="tablist" @keydown="handleKeyDown" :aria-label="ariaLabel">
      <li
        v-for="tab in tabs"
        :key="tab"
        class="nav-item"
        :ref="tab"
        :tabindex="isCurrentTab(tab) ? '0' : '-1'"
        role="tab"
        :id="tab.toLowerCase().replace(/\s/g, '-') + '-tab'"
        data-toggle="tab"
        @click="setTab(tab)"
        :aria-controls="tab.toLowerCase().replace(/\s/g, '-')"
        :aria-selected="tab === selected ? 'true' : 'false'"
        :class="{
          active: tab === selected,
          ['text-primary']: tab !== selected,
        }"
      >
        <a
          class="nav-link wc-tab-link font-weight-bold link-primary"
          :class="{
            active: tab === selected,
            ['text-primary']: tab !== selected,
          }"
          >{{ tab }}</a
        >
      </li>
    </ul>

    <slot></slot>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { KEYBOARD_CONSTANTS } from '../../constants/AppConstants';

export default defineComponent({
  emits: ['selected'],
  props: {
    /* Holds all of the tabs data */
    tabs: {
      type: Array,
      required: true,
    },
    initialSelectedTab: {
      type: String,
    },
    ariaLabel: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selected: this.initialSelectedTab || this.tabs[0],
      currentFocusedTab: 0,
    };
  },
  watch: {
    initialSelectedTab: {
      immediate: true,
      handler() {
        if (this.initialSelectedTab) {
          this.selected = this.initialSelectedTab;
        }
      },
    },
  },
  methods: {
    setTab(tab) {
      this.selected = tab;
      this.$emit('selected', this.selected);
    },
    isCurrentTab(tab) {
      return tab === this.tabs[this.currentFocusedTab];
    },
    /*
     * Method to handle keyboard accessibility for navigation using arrows
     */
    handleKeyDown(event) {
      if (
        event.keyCode === KEYBOARD_CONSTANTS.ARROW_RIGHT ||
        event.keyCode === KEYBOARD_CONSTANTS.ARROW_LEFT ||
        event.keyCode === KEYBOARD_CONSTANTS.ENTER ||
        event.keyCode === KEYBOARD_CONSTANTS.HOME ||
        event.keyCode === KEYBOARD_CONSTANTS.END
      ) {
        const currentTabRef = this.tabs[this.currentFocusedTab];
        this.$refs[currentTabRef][0].tabindex = KEYBOARD_CONSTANTS.TABINDEX_NO_FOCUS;
        // Moving right by pressing right arrow
        if (event.keyCode === KEYBOARD_CONSTANTS.ARROW_RIGHT) {
          this.currentFocusedTab += 1;
          // If we're at the end, go to the start
          if (this.currentFocusedTab >= this.tabs.length) {
            this.currentFocusedTab = 0;
          }
        }
        // Moving left by pressing left arrow
        else if (event.keyCode === KEYBOARD_CONSTANTS.ARROW_LEFT) {
          this.currentFocusedTab -= 1;
          // If we're at the start, move to the end
          if (this.currentFocusedTab < 0) {
            this.currentFocusedTab = this.tabs.length - 1;
          }
        }
        // If home is pressed, move to start
        else if (event.keyCode === KEYBOARD_CONSTANTS.HOME) {
          this.currentFocusedTab = 0;
        }
        // If End is pressed, move to end
        else if (event.keyCode === KEYBOARD_CONSTANTS.END) {
          this.currentFocusedTab = this.tabs.length - 1;
        }
        // Select current tab using enter key
        else if (event.keyCode === KEYBOARD_CONSTANTS.ENTER) {
          this.setTab(this.tabs[this.currentFocusedTab]);
        }
        const nextTabRef = this.tabs[this.currentFocusedTab];
        this.$refs[nextTabRef][0].tabindex = KEYBOARD_CONSTANTS.TABINDEX_FOCUS;
        this.$refs[nextTabRef][0].focus();
      }
    },
  },
});
</script>

<style scoped>
.wc-tab-link {
  cursor: pointer;
  user-select: none;
}
</style>
